// This is a simplified version - in production, this would be on a server
import { createClient } from '@supabase/supabase-js';

class SerpScraper {
  constructor() {
    // Initialize the Supabase client for storing results
    this.supabase = createClient(
      import.meta.env.VITE_SUPABASE_URL,
      import.meta.env.VITE_SUPABASE_ANON_KEY
    );
    
    // API backup service endpoints
    this.apiEndpoints = {
      serp: import.meta.env.VITE_SERP_API_ENDPOINT || 'https://api.yourserpservice.com/v1/search',
      apiKey: import.meta.env.VITE_SERP_API_KEY
    };
  }

  /**
   * Main search method - tries Puppeteer endpoint first, falls back to API
   */
  async searchGoogle(keyword, location, options = {}) {
    try {
      // First, try to use the Puppeteer backend service
      const puppeteerResult = await this.searchWithPuppeteer(keyword, location);
      if (puppeteerResult.success) {
        return puppeteerResult;
      }
      
      // If Puppeteer fails, fall back to the API service
      console.log("Puppeteer search failed, falling back to API service");
      return await this.searchWithApi(keyword, location);
    } catch (error) {
      console.error("All search methods failed:", error);
      return {
        success: false,
        error: error.message,
        timestamp: new Date().toISOString(),
        keyword,
        location: location.city + ',' + location.state
      };
    }
  }

  /**
   * Search using the Puppeteer backend service
   */
  async searchWithPuppeteer(keyword, location) {
    try {
      // This would call your backend service where Puppeteer is running
      const response = await fetch(import.meta.env.VITE_PUPPETEER_ENDPOINT || '/api/serp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_PUPPETEER_API_KEY}`
        },
        body: JSON.stringify({
          keyword,
          location: {
            address: location.address,
            city: location.city,
            state: location.state,
            latitude: location.latitude,
            longitude: location.longitude
          }
        })
      });

      if (!response.ok) {
        throw new Error(`Puppeteer service error: ${response.status}`);
      }

      const data = await response.json();
      return {
        success: true,
        data: data,
        timestamp: new Date().toISOString(),
        source: 'puppeteer',
        keyword,
        location: location.city + ',' + location.state
      };
    } catch (error) {
      console.error("Puppeteer search error:", error);
      // Return failure but don't throw - allows fallback
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Search using the backup API service
   */
  async searchWithApi(keyword, location) {
    try {
      // Build the query parameters for the API
      const params = new URLSearchParams({
        q: keyword,
        location: `${location.city},${location.state}`,
        gl: 'us',
        hl: 'en',
        api_key: this.apiEndpoints.apiKey
      });

      // Call the backup API service
      const response = await fetch(`${this.apiEndpoints.serp}?${params.toString()}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`API service error: ${response.status}`);
      }

      const data = await response.json();
      
      // Transform API response to match our expected format
      const transformedData = {
        organic: data.organic_results?.map((item, index) => ({
          position: index + 1,
          title: item.title,
          url: item.link,
          snippet: item.snippet,
          domain: new URL(item.link).hostname
        })) || [],
        
        mapPack: data.local_results?.map((item, index) => ({
          position: index + 1,
          name: item.title,
          rating: item.rating,
          address: item.address
        })) || [],
        
        ads: data.ads?.map((item, index) => ({
          position: index + 1,
          title: item.title,
          url: item.link,
          domain: new URL(item.link).hostname
        })) || [],
        
        totalResults: data.search_information?.total_results
      };

      return {
        success: true,
        data: transformedData,
        timestamp: new Date().toISOString(),
        source: 'api',
        keyword,
        location: location.city + ',' + location.state
      };
    } catch (error) {
      console.error("API search error:", error);
      throw error; // Rethrow as this is our last resort
    }
  }

  /**
   * Store SERP results in the database
   */
  async storeResults(keywordId, locationId, serpResult) {
    try {
      // Get keyword-location tracking ID
      const { data: tracking, error: trackingError } = await this.supabase
        .from('keyword_location_tracking_seo_87a5cd9f')
        .select('id')
        .eq('keyword_id', keywordId)
        .eq('location_id', locationId)
        .single();
      
      if (trackingError) throw trackingError;
      const trackingId = tracking.id;
      const today = new Date().toISOString().split('T')[0];

      // Find client's ranking in organic results
      let clientRank = null;
      let clientUrl = null;
      // This would need to be enhanced to match client's website
      // For now, we'll use a placeholder logic
      
      // Check if client appears in map pack
      let mapPackPosition = null;

      // Insert or update ranking record
      const { error: rankingError } = await this.supabase
        .from('rankings_seo_87a5cd9f')
        .upsert({
          keyword_location_id: trackingId,
          rank: clientRank || 999, // Use 999 if not found in top results
          map_pack_present: (serpResult.data.mapPack?.length > 0) || false,
          map_pack_position: mapPackPosition,
          date_checked: today,
          url_ranking: clientUrl,
          serp_data: serpResult.data, // Store full SERP data
          total_results: serpResult.data.totalResults,
          organic_results_count: serpResult.data.organic?.length || 0,
          map_pack_results_count: serpResult.data.mapPack?.length || 0,
          ads_count: serpResult.data.ads?.length || 0,
          source: serpResult.source
        }, 
        { onConflict: 'keyword_location_id,date_checked' });
      
      if (rankingError) throw rankingError;
      
      console.log(`Stored SERP results for tracking ID: ${trackingId}`);
      return true;
    } catch (error) {
      console.error('Error storing SERP results:', error);
      throw error;
    }
  }
}

export default SerpScraper;